/**
 * HotelsContext — Application-level cache for the Hotels page.
 *
 * Strategy:
 *  • On mount, silently prefetch the default view (Hotels / UK / default amenities)
 *    so the Hotels page loads instantly on first visit.
 *  • Every fetch made by Hotels.js is stored here; return visits show data immediately.
 *  • Cache TTL: 10 minutes.  Duplicate in-flight requests are deduplicated.
 */

import React, { createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { rateHawkService } from '../api/ratehawk';
import { FASTAPI_BASE } from '../constants';

const HotelsContext = createContext(null);

// ── Constants ────────────────────────────────────────────────────────────────
const CACHE_TTL      = 10 * 60 * 1000; // 10 minutes
const FETCH_SIZE     = 100;

// ── Cache key ────────────────────────────────────────────────────────────────
const makeCacheKey = (kind, country, filters, stars) =>
    `${kind}|${country}|${[...filters].sort().join(',')}|${[...stars].sort().join(',')}`;

// ── Reusable helpers (same logic as Hotels.js) ───────────────────────────────
export const normalizeInfoResponse = (json) => {
    const root      = json && typeof json === 'object' ? json : null;
    const candidate = root?.data ?? root;
    const byId      = {};
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        for (const [k, v] of Object.entries(candidate)) {
            if (!v || typeof v !== 'object') continue;
            const id = String(v.id ?? v.hotel_id ?? v.hid ?? k).trim();
            if (id) byId[id] = v;
        }
    } else if (Array.isArray(candidate)) {
        for (const item of candidate) {
            if (!item || typeof item !== 'object') continue;
            const id = String(item.id ?? item.hotel_id ?? item.hid ?? '').trim();
            if (id) byId[id] = item;
        }
    }
    return byId;
};

const fetchBatch = async (ids) => {
    if (!ids.length) return {};
    try {
        const res = await fetch(`${FASTAPI_BASE}/get-hotels-info`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ hotel_ids: ids }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return normalizeInfoResponse(await res.json());
    } catch (err) {
        console.error('[HotelsCache] batch fetch failed:', err.message);
        return {};
    }
};

// ── Provider ─────────────────────────────────────────────────────────────────
export const HotelsProvider = ({ children }) => {
    const cacheMap = useRef(new Map()); // key → { hotels: [], ts: number }
    const inFlight = useRef(new Set()); // prevent duplicate parallel fetches

    /** Return cached hotels or null if stale / missing */
    const get = useCallback((kind, country, filters, stars) => {
        const k     = makeCacheKey(kind, country, filters, stars);
        const entry = cacheMap.current.get(k);
        if (!entry) return null;
        if (Date.now() - entry.ts > CACHE_TTL) { cacheMap.current.delete(k); return null; }
        return entry.hotels;
    }, []);

    /** Store a fetched hotels array in the cache */
    const set = useCallback((kind, country, filters, stars, hotels) => {
        cacheMap.current.set(
            makeCacheKey(kind, country, filters, stars),
            { hotels, ts: Date.now() }
        );
    }, []);

    /** Silently fetch + cache a combination (no-op if already cached or in-flight) */
    const prefetch = useCallback(async (kind, country, filters = [], stars = []) => {
        const k = makeCacheKey(kind, country, filters, stars);
        if (cacheMap.current.has(k) || inFlight.current.has(k)) return;
        inFlight.current.add(k);
        try {
            const body = { kind: [kind], country: [country], preferable: true, top: true };
            if (filters.length) body.serp_filters = filters;
            if (stars.length)   body.star_rating  = stars;

            const { ids, hids } = await rateHawkService.getHotelIdsByFilter(body);
            if (!ids.length) return;

            const batch     = ids.slice(0, FETCH_SIZE);
            const batchHids = hids.slice(0, FETCH_SIZE);
            const infoById  = await fetchBatch(batch);
            const hotels    = batch.map(id => infoById[id]).filter(Boolean).map(h => ({ ...h, _reviews: [] }));

            cacheMap.current.set(k, { hotels, ts: Date.now() });

            // Back-fill reviews without blocking the cache entry
            const hidsToFetch = batchHids.length
                ? batchHids
                : hotels.map(h => h.hid ?? h.rg_ext?.id).filter(n => n != null).map(Number);

            if (hidsToFetch.length) {
                rateHawkService.getHotelReviewsByIds(hidsToFetch).then(reviewsByHid => {
                    const entry = cacheMap.current.get(k);
                    if (!entry) return;
                    const updated = entry.hotels.map((h, i) => {
                        const hid = batchHids[i] ?? h.hid ?? h.rg_ext?.id;
                        return { ...h, _reviews: (hid != null ? reviewsByHid[Number(hid)] : null) || [] };
                    });
                    cacheMap.current.set(k, { hotels: updated, ts: entry.ts });
                }).catch(() => {});
            }
        } catch (err) {
            console.error('[HotelsCache] prefetch failed:', err.message);
        } finally {
            inFlight.current.delete(k);
        }
    }, []);

    // Prefetch is triggered on-demand by Hotels.js when the user visits the Hotels page.
    // Memoised: App re-renders on every navigation, and a fresh object here
    // re-rendered every consumer (memoised cards included) each time.
    const hotelsValue = useMemo(() => ({ get, set, prefetch }), [get, set, prefetch]);

    return (
        <HotelsContext.Provider value={hotelsValue}>
            {children}
        </HotelsContext.Provider>
    );
};

export const useHotelsCache = () => useContext(HotelsContext);
